import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { forwardRef, useContext, useRef, useState, type ButtonHTMLAttributes, type SVGProps } from 'react'
import { ArrowUpRight, Template, Upload } from '@/assets/icons'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/button/Button'
import { FileChip } from '@/components/file-chip/FileChip'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { SelectButton } from '@/components/select-button/SelectButton'
import type { SelectedTemplate } from './types'
import { SELECT_TEMPLATE_MODAL_TAB, type SelectTemplateModalInitialTab } from './SelectTemplateModal'
import { matterPaths } from '@/routing/paths'
import { useExternalStorageMenuStatus } from '@/user_files/external-storage/hooks/useExternalStorageMenuStatus'
import { ExternalStorageMenuItems } from '@/user_files/external-storage/components/ExternalStorageMenuItems'
import { FilePickerSecurityFooter } from '@/user_files/external-storage/components/FilePickerSecurityFooter'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'

export type AddTemplateButtonProps = {
  template?: SelectedTemplate
  /** Name of a template uploading in the background; shows a spinner and lets the user still change it. */
  uploadingName?: string
  /** The selected template's formatting has been applied to the current draft — shows the "Template Applied" state. */
  applied?: boolean
  matterId?: string | null
  onUpload: (file: File) => void
  onOpenSelectTemplate: (initialTab?: SelectTemplateModalInitialTab) => void
  onRemove: () => void
  defaultOpen?: boolean
  /** Replaces the download wording when templates are applied by Export as .docx instead. */
  hint?: string
}

function UploadingSpinnerIcon({ className }: SVGProps<SVGSVGElement>) {
  return <span className={`${className ?? ''} animate-spin rounded-full border-2 border-current border-t-transparent opacity-70`} aria-hidden />
}

const TriggerButton = forwardRef<
  HTMLButtonElement,
  { isSelected: boolean; applied?: boolean; ariaHasPopup: 'dialog' | 'menu'; uploadingName?: string } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
>(({ isSelected, applied, ariaHasPopup, uploadingName, ...rest }, ref) => {
  if (uploadingName) {
    return (
      <SelectButton
        ref={ref}
        color="blue"
        leadingIcon={UploadingSpinnerIcon}
        aria-haspopup={ariaHasPopup}
        aria-label="Uploading template"
        hideLabelBelow={480}
        {...rest}
      >
        <span className="max-w-[90px] truncate" title={uploadingName}>
          {uploadingName}
        </span>
      </SelectButton>
    )
  }

  const label = applied ? 'Template Applied' : isSelected ? 'Template Added' : 'Add Template'
  return (
    <SelectButton
      ref={ref}
      color={isSelected ? 'blue' : 'neutral'}
      leadingIcon={Template}
      aria-haspopup={ariaHasPopup}
      aria-label={label}
      hideLabelBelow={480}
      {...rest}
    >
      {label}
    </SelectButton>
  )
})

export function AddTemplateButton({
  template,
  uploadingName,
  applied = false,
  matterId,
  onUpload,
  onOpenSelectTemplate,
  onRemove,
  defaultOpen = false,
  hint,
}: AddTemplateButtonProps) {
  const [open, setOpen] = useState(defaultOpen)
  const [showMenu, setShowMenu] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getDriveSyncOrganizationId(userAccountData)
  const externalStorageAction = useExternalStorageMenuStatus({
    matterId,
    menuOpen: open,
  })
  const connectedProviderLabel = externalStorageAction.connectedProviderMeta?.label

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    onUpload(file)
    e.target.value = ''
  }

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".docx" className="hidden" onChange={handleFileChange} />

      {template && !uploadingName && !showMenu ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <TriggerButton isSelected applied={applied} ariaHasPopup="dialog" />
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="end"
            sideOffset={8}
            width="sm"
            className="flex flex-col gap-3 pt-5 pb-4 px-5"
            // Don't auto-focus the first action on open — a pointer-opened popover would briefly
            // flash the Remove button's focus ring, and focus shouldn't land on a destructive action.
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex flex-col gap-2">
              <FileChip name={template.name} variant="template" />
              <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-950">
                {hint ??
                  (applied
                    ? 'Template formatting has been applied to this draft and is included when you download or save.'
                    : 'Template formatting will be automatically applied when you download your document.')}
              </p>
            </div>
            <div className="-mx-2 flex items-center gap-2">
              <Button
                variant="plain"
                color="neutral"
                size="xs"
                onClick={() => {
                  onRemove()
                  setOpen(false)
                }}
              >
                Remove
              </Button>
              <Button
                variant="plain"
                color="primary"
                size="xs"
                trailingIcon={<ArrowUpRight />}
                onClick={() => {
                  setShowMenu(true)
                  setOpen(true)
                }}
              >
                Change Template
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <DropdownMenu.Root
          open={open}
          onOpenChange={(next) => {
            setOpen(next)
            if (!next) setShowMenu(false)
          }}
        >
          <DropdownMenu.Trigger asChild>
            {/* Keep `applied` while changing templates so the label holds at "Template Applied" until
                a genuinely different template is confirmed — picking the same one shouldn't flip it. */}
            <TriggerButton isSelected={!!template} applied={applied} uploadingName={uploadingName} ariaHasPopup="menu" />
          </DropdownMenu.Trigger>

          {!externalStorageAction.loading && (
            <MenuContent side="bottom" align="end" sideOffset={8} className="w-[calc(100vw-24px)] max-w-[330px]">
              <div className="mx-4 mt-4 flex items-center rounded-lg border border-brand-neutral-200 bg-brand-neutral-50 px-3 py-3 text-xs font-medium leading-4 text-brand-neutral-600">
                {hint ?? 'Select a .docx template to apply formatting at download. Your draft will not be regenerated.'}
              </div>
              <MenuItemGroup>
                <MenuItem label="Upload" icon={Upload} variant="neutral" onSelect={() => fileInputRef.current?.click()} />
                <MenuItem
                  label="Select template"
                  icon={Template}
                  variant="neutral"
                  onSelect={() => onOpenSelectTemplate(SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES)}
                />
                <ExternalStorageMenuItems
                  status={externalStorageAction}
                  openExternalFilesLabel={connectedProviderLabel ? `Select template from ${connectedProviderLabel}` : undefined}
                  onOpenExternalFiles={() => onOpenSelectTemplate(SELECT_TEMPLATE_MODAL_TAB.EXTERNAL_FILES)}
                  onConnectFolder={() => {
                    if (!matterId) return
                    // This menu lives under the chat route, which would otherwise tag the event
                    // with feature: 'agent'. trackMatterDriveSyncEvent's explicit feature +
                    // suppressFeatureInheritance wins over route inheritance in useAnalytics, so
                    // Drive Sync events are always tagged 'matter_drive_sync' regardless of
                    // call-site route.
                    trackMatterDriveSyncEvent(
                      AnalyticsEvent.MatterDriveSyncSetupEntryClicked,
                      {
                        matterId,
                        organizationId,
                        matterDriveSyncEntrySource: MatterDriveSyncEntrySource.AddTemplateButton,
                        ...(externalStorageAction.displayProvider ? { externalStorageProvider: externalStorageAction.displayProvider } : {}),
                      },
                      trackEvent
                    )
                    navigate(matterPaths.settings(matterId))
                  }}
                />
              </MenuItemGroup>
              <FilePickerSecurityFooter variant={externalStorageAction.footerVariant} onNavigate={() => setOpen(false)} />
            </MenuContent>
          )}
        </DropdownMenu.Root>
      )}
    </>
  )
}
